import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'

export default function RateCalculator({ session, theme, canEdit = true }) {
  const [profiles, setProfiles] = useState([])
  const [activeProfileId, setActiveProfileId] = useState(null)
  const [profileName, setProfileName] = useState('')
  const [income, setIncome] = useState('')
  const [hoursPerWeek, setHoursPerWeek] = useState('')
  const [weeksPerYear, setWeeksPerYear] = useState('')
  const [wordsPerProject, setWordsPerProject] = useState('')
  const [hoursPerProject, setHoursPerProject] = useState('')
  const [saving, setSaving] = useState(false)
  const [incomeFromProfile, setIncomeFromProfile] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)
  const [newProfileName, setNewProfileName] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const annualIncome = parseFloat(income) || 0
  const hours = parseFloat(hoursPerWeek) || 0
  const weeks = parseFloat(weeksPerYear) || 0
  const totalHours = hours * weeks
  const hourlyRate = totalHours > 0 ? annualIncome / totalHours : 0
  const perWordRate = wordsPerProject > 0 && hourlyRate > 0
    ? (hourlyRate * (parseFloat(hoursPerProject) || 1)) / parseFloat(wordsPerProject)
    : 0
  const perProjectRate = hoursPerProject > 0 && hourlyRate > 0
    ? hourlyRate * parseFloat(hoursPerProject)
    : 0
  const hasResult = hourlyRate > 0

  useEffect(() => {
    async function loadOnMount() {
      const { data } = await supabase
        .from('rate_profiles')
        .select('*')
        .order('created_at', { ascending: true })
      if (data) {
        setProfiles(data)
        if (data.length > 0) {
          loadProfile(data[0])
        } else if (session?.user?.id) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('target_income')
            .eq('user_id', session.user.id)
            .single()
          if (profileData?.target_income) {
            setIncome(String(profileData.target_income))
            setIncomeFromProfile(true)
          }
        }
      }
    }
    loadOnMount()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function fetchProfiles() {
    const { data } = await supabase
      .from('rate_profiles')
      .select('*')
      .order('created_at', { ascending: true })
    if (data) {
      setProfiles(data)
      if (data.length > 0 && !activeProfileId) {
        loadProfile(data[0])
      }
    }
  }

  function loadProfile(profile) {
    setActiveProfileId(profile.id)
    setProfileName(profile.name)
    setIncome(profile.income?.toString() || '')
    setHoursPerWeek(profile.hours_per_week?.toString() || '')
    setWeeksPerYear(profile.weeks_per_year?.toString() || '')
    setWordsPerProject('')
    setHoursPerProject('')
  }

  async function saveProfile() {
    setSaving(true)
    if (!activeProfileId) {
      const name = profileName.trim() || 'My Profile'
      const { data } = await supabase
        .from('rate_profiles')
        .insert({
          user_id: session.user.id,
          name,
          income: parseFloat(income) || 0,
          hours_per_week: parseFloat(hoursPerWeek) || 0,
          weeks_per_year: parseFloat(weeksPerYear) || 0,
        })
        .select()
        .single()
      if (data) {
        await fetchProfiles()
        loadProfile(data)
      }
    } else {
      await supabase
        .from('rate_profiles')
        .update({
          name: profileName,
          income: parseFloat(income) || 0,
          hours_per_week: parseFloat(hoursPerWeek) || 0,
          weeks_per_year: parseFloat(weeksPerYear) || 0,
        })
        .eq('id', activeProfileId)
      await fetchProfiles()
    }
    setSaving(false)
  }

  async function createProfile() {
    if (!newProfileName.trim()) return
    setSaving(true)
    const { data } = await supabase
      .from('rate_profiles')
      .insert({
        user_id: session.user.id,
        name: newProfileName.trim(),
        income: 0,
        hours_per_week: 0,
        weeks_per_year: 0,
      })
      .select()
      .single()
    if (data) {
      await fetchProfiles()
      loadProfile(data)
    }
    setNewProfileName('')
    setShowNewForm(false)
    setSaving(false)
  }

  async function deleteProfile(id) {
    await supabase.from('rate_profiles').delete().eq('id', id)
    setDeleteConfirm(null)
    const remaining = profiles.filter(p => p.id !== id)
    if (remaining.length > 0) {
      loadProfile(remaining[0])
    } else {
      setActiveProfileId(null)
      setProfileName('')
      setIncome('')
      setHoursPerWeek('')
      setWeeksPerYear('')
    }
    await fetchProfiles()
  }

  const inputStyle = {
    background: theme.surfaceAlt || theme.surface,
    border: `1px solid ${theme.border}`,
    color: theme.text,
    fontFamily: "'Nunito Sans', sans-serif",
    fontSize: 14,
    padding: '10px 14px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    borderRadius: 2,
  }

  const labelStyle = {
    fontSize: 11,
    color: theme.textMuted,
    fontFamily: "'Open Sans', sans-serif",
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: 6,
    display: 'block',
  }

  const cardStyle = {
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    padding: '28px 32px',
    marginBottom: 20,
  }

  const statCardStyle = (highlight) => ({
    background: highlight ? `${theme.accent}15` : theme.surface,
    border: `1px solid ${highlight ? theme.accent : theme.border}`,
    borderLeft: highlight ? `4px solid ${theme.accent}` : `1px solid ${theme.border}`,
    padding: '20px 24px',
    flex: 1,
  })

  const btnStyle = (primary) => ({
    background: primary ? theme.accent : 'transparent',
    border: `1px solid ${primary ? theme.accent : theme.border}`,
    color: primary ? '#fff' : theme.textMuted,
    fontFamily: "'Open Sans', sans-serif",
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '8px 16px',
    cursor: 'pointer',
    borderRadius: 2,
  })

  return (
    <div>
      {/* Section header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{
          fontSize: 11, color: theme.accent,
          fontFamily: "'Open Sans', sans-serif",
          letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4,
        }}>Rate Calculator</div>
        <div style={{ fontSize: 14, color: theme.textSecondary, fontFamily: "'Nunito Sans', sans-serif" }}>
          Figure out what to charge so your writing business actually pays the bills.
        </div>
      </div>

      {/* Profile switcher */}
      <div style={{
        ...cardStyle,
        padding: '20px 32px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <div style={{
          fontSize: 11, color: theme.textMuted,
          fontFamily: "'Open Sans', sans-serif",
          letterSpacing: '0.1em', textTransform: 'uppercase',
          marginRight: 4, flexShrink: 0,
        }}>Goal Profile:</div>

        {profiles.map(p => (
          <button
            key={p.id}
            onClick={() => loadProfile(p)}
            style={{
              background: activeProfileId === p.id ? `${theme.accent}20` : 'transparent',
              border: `1px solid ${activeProfileId === p.id ? theme.accent : theme.border}`,
              color: activeProfileId === p.id ? theme.accent : theme.textMuted,
              fontFamily: "'Nunito Sans', sans-serif",
              fontSize: 13,
              fontWeight: activeProfileId === p.id ? 700 : 400,
              padding: '6px 16px',
              cursor: 'pointer',
              borderRadius: 2,
            }}
          >
            {p.name}
          </button>
        ))}

        {canEdit && (showNewForm ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              autoFocus
              placeholder="Profile name..."
              value={newProfileName}
              onChange={e => setNewProfileName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') createProfile(); if (e.key === 'Escape') setShowNewForm(false) }}
              style={{ ...inputStyle, width: 160, padding: '6px 12px', fontSize: 13 }}
            />
            <button onClick={createProfile} style={btnStyle(true)}>Create</button>
            <button onClick={() => setShowNewForm(false)} style={btnStyle(false)}>Cancel</button>
          </div>
        ) : (
          <button
            onClick={() => setShowNewForm(true)}
            style={{
              background: 'transparent',
              border: `1px dashed ${theme.border}`,
              color: theme.textMuted,
              fontFamily: "'Open Sans', sans-serif",
              fontSize: 11,
              letterSpacing: '0.08em',
              padding: '6px 14px',
              cursor: 'pointer',
              borderRadius: 2,
            }}
          >
            + New Profile
          </button>
        ))}
      </div>

      {/* Input card */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <input
              value={profileName}
              onChange={e => setProfileName(e.target.value)}
              placeholder="Profile name..."
              disabled={!canEdit}
              style={{
                ...inputStyle,
                width: 'auto',
                fontSize: 18,
                fontFamily: "'Cardo', serif",
                background: 'transparent',
                border: '1px solid transparent',
                borderBottom: `1px solid ${theme.border}`,
                borderRadius: 0,
                padding: '4px 0',
              }}
            />
            <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", marginTop: 4 }}>
              Edit the name inline — it saves with your numbers
            </div>
          </div>

          {canEdit && (
            <div style={{ display: 'flex', gap: 8 }}>
              {activeProfileId && deleteConfirm === activeProfileId ? (
                <>
                  <span style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", alignSelf: 'center' }}>
                    Delete this profile?
                  </span>
                  <button onClick={() => deleteProfile(activeProfileId)} style={{ ...btnStyle(false), color: '#e05c5c', borderColor: '#e05c5c' }}>Yes, delete</button>
                  <button onClick={() => setDeleteConfirm(null)} style={btnStyle(false)}>Cancel</button>
                </>
              ) : (
                <>
                  {activeProfileId && (
                    <button onClick={() => setDeleteConfirm(activeProfileId)} style={btnStyle(false)}>Delete</button>
                  )}
                  <button
                    onClick={saveProfile}
                    disabled={saving}
                    style={btnStyle(true)}
                  >
                    {saving ? 'Saving...' : 'Save Profile'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
          <div>
            <label style={labelStyle}>Target Annual Income ($)</label>
            <input
              type="number"
              placeholder="e.g. 80000"
              value={income}
              onChange={e => { setIncome(e.target.value); setIncomeFromProfile(false) }}
              style={inputStyle}
              disabled={!canEdit}
            />
            {incomeFromProfile && (
              <div style={{ fontSize: 11, color: theme.accent, fontFamily: "'Nunito Sans', sans-serif", marginTop: 4, opacity: 0.8 }}>
                Pre-filled from your Profile
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Billable Hours / Week</label>
            <input
              type="number"
              placeholder="e.g. 25"
              value={hoursPerWeek}
              onChange={e => setHoursPerWeek(e.target.value)}
              style={inputStyle}
              disabled={!canEdit}
            />
          </div>
          <div>
            <label style={labelStyle}>Weeks Worked / Year</label>
            <input
              type="number"
              placeholder="e.g. 48"
              value={weeksPerYear}
              onChange={e => setWeeksPerYear(e.target.value)}
              style={inputStyle}
              disabled={!canEdit}
            />
          </div>
        </div>

        {!activeProfileId && (
          <div style={{ marginTop: 16, fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>
            Enter a name above and hit Save Profile to save this scenario.
          </div>
        )}
      </div>

      {/* Results row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        <div style={statCardStyle(true)}>
          <div style={{
            fontSize: 10, color: theme.accent, letterSpacing: '0.12em',
            textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8,
          }}>Your Minimum Hourly Rate</div>
          <div style={{ fontSize: 38, color: theme.text, fontFamily: "'Cardo', serif", lineHeight: 1, marginBottom: 6 }}>
            {hasResult ? `$${hourlyRate.toFixed(2)}` : '—'}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>
            {hasResult ? `Based on ${totalHours.toLocaleString()} billable hours/year` : 'Fill in all three fields above'}
          </div>
        </div>

        <div style={statCardStyle(false)}>
          <div style={{
            fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em',
            textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8,
          }}>Monthly Target</div>
          <div style={{ fontSize: 38, color: theme.text, fontFamily: "'Cardo', serif", lineHeight: 1, marginBottom: 6 }}>
            {annualIncome > 0 ? `$${(annualIncome / 12).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>per month to hit your goal</div>
        </div>

        <div style={statCardStyle(false)}>
          <div style={{
            fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em',
            textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8,
          }}>Weekly Target</div>
          <div style={{ fontSize: 38, color: theme.text, fontFamily: "'Cardo', serif", lineHeight: 1, marginBottom: 6 }}>
            {annualIncome > 0 && weeks > 0
              ? `$${(annualIncome / weeks).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
              : '—'}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>per week you work</div>
        </div>
      </div>

      {/* Project estimator */}
      <div style={cardStyle}>
        <div style={{ fontSize: 18, color: theme.text, fontFamily: "'Cardo', serif", marginBottom: 6 }}>
          Project Rate Estimator
        </div>
        <div style={{ fontSize: 13, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", marginBottom: 20 }}>
          Enter a project's details to see what you should charge.
          {!hasResult && <span style={{ color: theme.accent }}> Calculate your hourly rate first.</span>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          <div>
            <label style={labelStyle}>Estimated Hours for This Project</label>
            <input
              type="number"
              placeholder="e.g. 3"
              value={hoursPerProject}
              onChange={e => setHoursPerProject(e.target.value)}
              style={inputStyle}
              disabled={!hasResult}
            />
          </div>
          <div>
            <label style={labelStyle}>Word Count (optional, for per-word rate)</label>
            <input
              type="number"
              placeholder="e.g. 1500"
              value={wordsPerProject}
              onChange={e => setWordsPerProject(e.target.value)}
              style={inputStyle}
              disabled={!hasResult}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <div style={statCardStyle(perProjectRate > 0)}>
            <div style={{
              fontSize: 10, color: perProjectRate > 0 ? theme.accent : theme.textMuted,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              fontFamily: "'Open Sans', sans-serif", marginBottom: 8,
            }}>Charge for This Project</div>
            <div style={{ fontSize: 32, color: theme.text, fontFamily: "'Cardo', serif", lineHeight: 1, marginBottom: 4 }}>
              {perProjectRate > 0 ? `$${perProjectRate.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>flat project fee</div>
          </div>

          <div style={statCardStyle(perWordRate > 0)}>
            <div style={{
              fontSize: 10, color: perWordRate > 0 ? theme.accent : theme.textMuted,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              fontFamily: "'Open Sans', sans-serif", marginBottom: 8,
            }}>Per-Word Rate</div>
            <div style={{ fontSize: 32, color: theme.text, fontFamily: "'Cardo', serif", lineHeight: 1, marginBottom: 4 }}>
              {perWordRate > 0 ? `$${perWordRate.toFixed(3)}` : '—'}
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>per word</div>
          </div>
        </div>
      </div>

      {/* Helper note */}
      <div style={{
        fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif",
        fontStyle: 'italic', lineHeight: 1.6, padding: '0 4px',
      }}>
        💡 These are your <strong>floor rates</strong> — the minimum you need to hit your income goal.
        Always quote above your floor to leave room for scope creep, revisions, and taxes.
      </div>
    </div>
  )
}